/* GASSISA
=========================================================================================
* Descripción: Módulo de entradas de inventario por Almacén o Exhibidor
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 29/01/2025 | Lic. José Luis Aragón Cervantes  | Creación
*      2       | 31/05/2026 | Lic. José Luis Aragón Cervantes  | Redirección estable por ventana duplicada
*      3       | 08/06/2026 | Lic. José Luis Aragón Cervantes  | Aumento de fuente en lista de existencias
 *      4       | 08/06/2026 | Lic. José Luis Aragón Cervantes  | Pie flotante y tabla sin paginado
*      5       | 11/06/2026 | Lic. José Luis Aragón Cervantes  | Orden alfabético por descripción al cargar filtro
*      6       | 03/07/2026 | Lic. José Luis Aragón Cervantes  | Envía credenciales cargadas de caja al flujo WhatsApp
 *      7       | 26/08/2026 | Lic. José Luis Aragón Cervantes  | Acción directa a verificador de precios desde existencias
 *      8       | 18/09/2026 | LIC. José Luis Aragón C.                       | Portétil: clave+descripción con scroll, solo totales fijo
 *      9       | 23/09/2026 | Lic. José Luis Aragón Cervantes  | Filtro opcional de corrida para limitar medidas (rango inicial-final vía backend)
 *      10      | 23/09/2026 | Lic. José Luis Aragón Cervantes  | Corrida limpiable con permanencia en localStorage
 *      11      | 23/09/2026 | LIC. José Luis Aragón C.                       | Checker opcional "Solo con existencias" (apagado = sin cambios)
 *      12      | 25/09/2026 | LIC. José Luis Aragón C.                       | Re-consulta al enfocar + hora de
 *              |            |                                  | última actualización (traslados)
 *      13      | 10/10/2026 | LIC. José Luis Aragón C.                       | Portétil: descripción completa del renglón en campo superior de la tabla
 *      14      | 10/10/2026 | Lic. José Luis Aragón C.                       | Push-to-talk: el micrófono se escucha mientras se mantiene presionado y entrega el texto al soltar
 * ---------------------------------------------------------------------------------------
 */

import TextField from "../ui/TextField";
import Autocomplete from "../ui/Autocomplete";
import { Fragment, useEffect, useState, useRef, useMemo } from 'react'
import { Modal, Box, Checkbox, FormControlLabel } from "@mui/material";

import { defaultTypes } from "../../settings/enums.js";
import { getDateString, getDateStringV2 } from "../../settings/utils.js"
import { apiObtenerCorridas } from "../../settings/ApiConfig.js";

import CancelIcon from '@mui/icons-material/Cancel';
import PlaylistRemoveIcon from '@mui/icons-material/PlaylistRemove';
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from '@mui/icons-material/Remove';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import IconButton from '@mui/material/IconButton';

import MnuExistenciasAlmacenesExhibidoresActions from "./MnuExistenciasAlmacenesExhibidoresActions.jsx"
import SizeSoftXLectorCodigoBarrasGeneral from "../leyout/SizeSoftXLectorCodigoBarrasGeneral.jsx"
import SizeSoftXAlert from "../utils/SizeSoftXAlert.jsx"
import SizeSoftXLayoutForm from "../leyout/SizeSoftXLayoutForm.jsx"
import SizeSoftXTableVentas from "../leyout/SizeSoftXTableVentas.jsx"
import MnuComponentActions from "../utils/MnuComponentActions.jsx"
import SemaforoStatus from "../utils/SemaforoStatus.jsx"
import SizeSoftXTableExistencias from "../../components/leyout/SizeSoftXTableExistencias"
import SizeSoftXLoadingProcess from "../../components/leyout/SizeSoftXLoadingProcess"
import WhatsAppEnvioModal from "../utils/WhatsAppEnvioModal.jsx"

import { useRequest } from "../../context/System/RequestContext.js";
import { useSesion } from "../../context/System/SesionContext.js";
import { useInventarios } from "../../context/Inventarios/InventariosContext.js"
import { useLayout } from "../../context/System/LayoutContext.js"
import { useFormTabLock } from "../../hooks/useFormTabLock";
import useSpeechToText from "../leyout/useSpeechToText";
import BotonVoz from "../leyout/BotonVoz";
import { useNavigate } from "react-router-dom";

export default function FrmExistencias() {
    const navigate = useNavigate();
    const { isLocked: isFormLocked } = useFormTabLock('existencias', {
        title: 'Existencias',
        onLockDetected: (info) => {
            console.warn(`El formulario de ${info?.title || 'Existencias'} ya está abierto en otra pestaña`);
        }
    });

    const {
        listaAlmacenesExhibidoresGeneral,
        handleObtenerAlmacenesExhibidores,
        handleObtenerExistenciasAlmacenExhibidor,
        dataFiltroExistencias,
        claveArticulo, medida,
        setClaveArticulo, setMedida,
        statusWaiting, setStatusWaiting,
        handleClickRegresarMainDashboard,
    } = useInventarios();

    const { handleOpenAlert } = useLayout();

    const { isSupported, hasPermission, isListening, error: speechError, startListening, stopListening, checkPermission } = useSpeechToText({
        onResult: (text) => {
            const cleaned = text.replace(/[.]+$/, '').trim();
            setFiltro(cleaned);
            setStatusWaiting(true);
            handleObtenerExistenciasAlmacenExhibidor(cleaned, almacenexhibidor, corridaSeleccionada).finally(() => setStatusWaiting(false));
            setTimeout(() => document.getElementById('idFiltro')?.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true })), 100);
        }
    });

    useEffect(() => {
        checkPermission();
    }, [checkPermission]);

    const [openModalWhatsApp, setOpenModalWhatsApp] = useState(false);
    const [showClaveColumn, setShowClaveColumn] = useState(false);
    const [isPortatil, setIsPortatil] = useState(false);

    useEffect(() => {
        const check = () => setIsPortatil(!!document.querySelector('.sizesoftx-portatil'));
        check();
    }, []);

    useEffect(() => {
        if (isFormLocked) {
            handleOpenAlert("El formulario de Existencias ya está abierto en otra pestaña del navegador.\n\nPor favor, cierre la otra ventana o pestaña para continuar.", "error");
            setTimeout(() => {
                navigate("/", { replace: true });
            }, 900);
        }
    }, [isFormLocked, handleOpenAlert, navigate]);

    const { parametrosCajaForm } = useSesion();

    const { GetRequest, PostRequest } = useRequest()

    const [dataArticuloExistencia, setDataArticuloExistencia] = useState({});
    const [filtro, setFiltro] = useState("");
    const [almacenexhibidor, setAlmacenExhibidor] = useState(parametrosCajaForm.dominio);
    // Rev 12: hora de la última consulta (los traslados/ventas de otras
    // méquinas cambian existencias; el dato es al momento de consultar).
    const [ultimaActualizacion, setUltimaActualizacion] = useState(null);
    const filtroRef = useRef("");
    const almacenRef = useRef("");
    const corridaSeleccionadaRef = useRef("");
    filtroRef.current = filtro;
    almacenRef.current = almacenexhibidor;
    const [totalRegistros, setTotalRegistros] = useState(0);
    const [cantidadArticulos, setCantidadArticulos] = useState(0);
    // Checker opcional "Solo con existencias": filtrado en cliente sobre lo ya
    // consultado. Apagado = comportamiento actual sin cambios.
    const SOLO_EXISTENCIAS_STORAGE_KEY = "existencias_solo_con_existencias";
    const [soloConExistencias, setSoloConExistencias] = useState(() => {
        try {
            return localStorage.getItem(SOLO_EXISTENCIAS_STORAGE_KEY) === "1";
        } catch {
            return false;
        }
    });

    const handleChangeSoloConExistencias = (e) => {
        const valor = e.target.checked;
        setSoloConExistencias(valor);
        try {
            localStorage.setItem(SOLO_EXISTENCIAS_STORAGE_KEY, valor ? "1" : "0");
        } catch {
            return;
        }
    };

    const dataConExistencias = useMemo(() => {
        if (!dataFiltroExistencias) return [];
        if (!soloConExistencias) return dataFiltroExistencias;
        return dataFiltroExistencias.filter((item) =>
            (item.listamedidas || []).some((m) => (m.existencia || 0) > 0)
        );
    }, [dataFiltroExistencias, soloConExistencias]);
    // Filtro opcional de corrida (misma rutina que Artículos: se manda la
    // descripción y el backend resuelve medidainicial/medidafinal).
    // Persistente en localStorage para mantenerla al regresar al componente.
    // Vacío = todas las medidas.
    const CORRIDA_STORAGE_KEY = "existencias_corrida";
    const [listaCorridas, setListaCorridas] = useState([]);
    const [corridaSeleccionada, setCorridaSeleccionada] = useState(() => {
        try {
            return localStorage.getItem(CORRIDA_STORAGE_KEY) || "";
        } catch {
            return "";
        }
    });
    const [corridaInput, setCorridaInput] = useState(() => {
        try {
            return localStorage.getItem(CORRIDA_STORAGE_KEY) || "";
        } catch {
            return "";
        }
    });
    corridaSeleccionadaRef.current = corridaSeleccionada;

    const corridaRango = useMemo(() => {
        if (!corridaSeleccionada) return null;
        const found = listaCorridas.find((x) => x.descripcion === corridaSeleccionada);
        if (!found) return null;
        return { medidainicial: found.medidainicial, medidafinal: found.medidafinal };
    }, [corridaSeleccionada, listaCorridas]);

    const dataOrdenada = useMemo(() => {
        if (!dataConExistencias) return [];
        return [...dataConExistencias].sort((a, b) =>
            (a.descripcion?.trim() || '').localeCompare(b.descripcion?.trim() || '')
        );
    }, [dataConExistencias]);

    // Rev 13: en portétil la columna descripción se recorta a 2 líneas, por lo
    // que al hacer clic (o al moverse con flechas) en una fila se refleja aquí la
    // descripción completa para poder leerla sin abrir tooltips.
    const [descripcionSeleccionada, setDescripcionSeleccionada] = useState("");

    const handleArticuloSeleccionado = async (e) => {
        setDataArticuloExistencia(e);
        setDescripcionSeleccionada(e?.descripcion?.trim() || "");
    };

    const handleVerificarPreciosArticulo = (articulo) => {
        if (!articulo?.clavearticulo) return;
        setDataArticuloExistencia(articulo);
        navigate("/verificador", { state: { articuloVerificador: articulo } });
    };

    const handleChangeAlmacenexhibidor = async (e, newDat) => {
        setAlmacenExhibidor(newDat.nombre);
        setStatusWaiting(true);
        await handleObtenerExistenciasAlmacenExhibidor(filtro, newDat.nombre, corridaSeleccionada);
        setStatusWaiting(false);
    };

    const handleChangeFilter = (e) => {
        setFiltro(e.target.value);
        setDescripcionSeleccionada("");
    };

    const persistirCorrida = (valor) => {
        try {
            if (valor && valor.trim().length > 0) {
                localStorage.setItem(CORRIDA_STORAGE_KEY, valor);
            } else {
                localStorage.removeItem(CORRIDA_STORAGE_KEY);
            }
        } catch {
            return;
        }
    };

    const handleChangeCorrida = async (e, newValue) => {
        const valor = typeof newValue === "string" ? newValue : (newValue?.descripcion || "");
        setCorridaSeleccionada(valor);
        corridaSeleccionadaRef.current = valor;
        setCorridaInput(valor);
        persistirCorrida(valor);
        // Si ya hay un filtro capturado, re-consultar con el nuevo rango.
        if (filtro && filtro.trim().length > 0) {
            setStatusWaiting(true);
            await handleObtenerExistenciasAlmacenExhibidor(filtro, almacenexhibidor, valor);
            setStatusWaiting(false);
        }
    };

    const handleInputChangeCorrida = (e, newInputValue) => {
        setCorridaInput(newInputValue || "");
    };

    const handleBlurCorrida = () => {
        // Si borró el texto manualmente, confirmar limpieza (todas las medidas).
        if (!corridaInput || corridaInput.trim().length === 0) {
            if (corridaSeleccionada) handleLimpiarCorrida();
            else setCorridaInput("");
        }
    };

    const handleLimpiarCorrida = async () => {
        setCorridaSeleccionada("");
        setCorridaInput("");
        persistirCorrida("");
        if (filtro && filtro.trim().length > 0) {
            setStatusWaiting(true);
            await handleObtenerExistenciasAlmacenExhibidor(filtro, almacenexhibidor, "");
            setStatusWaiting(false);
        }
    };

    const handleBuscarArticulo = async (e) => {
        setFiltro(e.descripcion);
        setStatusWaiting(true);
        await handleObtenerExistenciasAlmacenExhibidor(e.descripcion, almacenexhibidor, corridaSeleccionada);
        setStatusWaiting(false);
    };

    const handleOnKeyDownCorrida = async (e) => {
        if (e.key === 'Enter') {
            // Igual que el filtro por descripción: confirma el texto capturado
            // como corrida y lanza la consulta si ya hay filtro.
            const valor = corridaInput ?? "";
            setCorridaSeleccionada(valor);
            setCorridaInput(valor);
            persistirCorrida(valor);
            if (filtro && filtro.trim().length > 0) {
                setStatusWaiting(true);
                await handleObtenerExistenciasAlmacenExhibidor(filtro, almacenexhibidor, valor);
                setStatusWaiting(false);
            }
        }
    };

    const handleOnKeyDowsFilter = async (e) => {
        if (e.key === 'Enter') {
            if (filtro.length === 0) {
                return;
            }
            setStatusWaiting(true);
            await handleObtenerExistenciasAlmacenExhibidor(filtro, almacenexhibidor, corridaSeleccionada);
            setStatusWaiting(false);
        }
    };

    useEffect(() => {
        const handleCargarCorridas = async () => {
            const result = await GetRequest({
                url: apiObtenerCorridas,
                loader: "Se estén obteniendo las corridas del sistema...",
            });
            if (result?.hasError) return;
            setListaCorridas(Array.isArray(result?.result) ? result.result : []);
        };
        handleCargarCorridas();
    }, []);

    useEffect(() => {
        if (listaAlmacenesExhibidoresGeneral.length === 0) {
            handleObtenerAlmacenesExhibidores("GENERAL");
        }
    }, []);

    useEffect(() => {
        if (Array.isArray(dataFiltroExistencias) && dataFiltroExistencias.length > 0) {
            setUltimaActualizacion(new Date());
        }
    }, [dataFiltroExistencias]);

    // Rev 12: al regresar a esta ventana (tras aplicar un traslado en otro
    // módulo) re-consulta el filtro actual para no mostrar datos viejos.
    useEffect(() => {
        let refrescando = false;
        const refrescarAlEnfocar = async () => {
            const f = (filtroRef.current || "").trim();
            const a = (almacenRef.current || "").trim();
            if (!f || !a || refrescando) return;
            refrescando = true;
            setStatusWaiting(true);
            try {
                await handleObtenerExistenciasAlmacenExhibidor(f, a, corridaSeleccionadaRef.current || "");
            } catch {
                // best-effort: conserva lo último consultado
            } finally {
                setStatusWaiting(false);
                refrescando = false;
            }
        };
        window.addEventListener("focus", refrescarAlEnfocar);
        return () => window.removeEventListener("focus", refrescarAlEnfocar);
    }, []);

    useEffect(() => {
        if (!dataConExistencias || dataConExistencias === null) {
            return;
        }

        let total = 0;
        dataConExistencias.forEach(item => {
            if (item.listamedidas) {
                item.listamedidas.forEach(m => {
                    total += m.existencia || 0;
                });
            }
        });
        
        setTotalRegistros(dataConExistencias.length);
        setCantidadArticulos(total);
    }, [dataConExistencias]);

    //Listas ordenadas para combos selectores    
    const listaAlmacenesExhibidoresOrdenada = listaAlmacenesExhibidoresGeneral?.slice().sort((c, d) => c.almacenexhibidor.localeCompare(d.almacenexhibidor));

    let listaAlmacenesExhibidoresAutoComplete = [];
    listaAlmacenesExhibidoresOrdenada.map((y) =>
        listaAlmacenesExhibidoresAutoComplete.push({
            id: y.id,
            nombre: y.almacenexhibidor,
        })
    );

    listaAlmacenesExhibidoresAutoComplete.push({
        id: -1,
        nombre: "FULL",
    });

    //Key actions windows
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key.trim().toUpperCase() === 'F4') {
                event.preventDefault();
                handleClickRegresarMainDashboard();
                return;
            }

            if (event.altKey && event.key === 'F1') {
                event.preventDefault();
                event.stopPropagation();
                const inputFiltro = document.getElementById('idFiltro');
                if (inputFiltro) {
                    inputFiltro.focus();
                    inputFiltro.select();
                }
                return;
            }

            if (event.key === 'F1') {
                event.preventDefault();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    const getAlmacenValue = () => {
        const found = listaAlmacenesExhibidoresAutoComplete.find(x => x.nombre === almacenexhibidor);
        return found || (almacenexhibidor === "FULL" ? { id: -1, nombre: "FULL" } : almacenexhibidor);
    };

    return (
        <>
            <Fragment>
                <SizeSoftXLayoutForm
                    titlePage="Existencias Almacenes/Exhibidores"
                    buttonActions={<MnuExistenciasAlmacenesExhibidoresActions listaParaSincronizar={dataFiltroExistencias} almacen={almacenexhibidor} onWhatsApp={() => setOpenModalWhatsApp(true)} />}
                >
                    <div className="conteiner-form-FrmExistencias">
                            <div className="body-form-FrmExistencias">
                                <Autocomplete
                                    freeSolo
                                    noOptionsText={"Sin resultados"}
                                    disableClearable
                                    id={"id"}
                                    name={"AlmacenExhibidor"}
                                    options={listaAlmacenesExhibidoresAutoComplete}
                                    getOptionLabel={(option) => typeof option === "string" ? option : option.nombre}
                                    value={getAlmacenValue()}
                                    onChange={(e, newDat) => handleChangeAlmacenexhibidor(e, newDat)}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Almacén o Exhibidor:" required />
                                    )}
                                    sx={{ minWidth: '200px', flex: 1 }}
                                />
                                <TextField autoComplete="off"
                                    id="idFiltro"
                                    name="filtro"
                                    label="Filtro:"
                                    value={filtro ?? ""}
                                    onChange={handleChangeFilter}
                                    onKeyDown={handleOnKeyDowsFilter}
                                    sx={{ minWidth: '200px', flex: 1 }}
                                />
                                <BotonVoz
                                        isSupported={isSupported}
                                        hasPermission={hasPermission}
                                        isListening={isListening}
                                        onStart={startListening} onStop={stopListening}
                                    />
                                <Box sx={{ minWidth: '220px', flex: 1, display: 'flex', alignItems: 'flex-start', gap: '4px' }}>
                                    <Autocomplete
                                        freeSolo
                                        clearOnEscape
                                        disableClearable={false}
                                        noOptionsText={"Sin resultados"}
                                        id={"idCorrida"}
                                        name={"Corrida"}
                                        options={listaCorridas}
                                        getOptionLabel={(option) => typeof option === "string" ? option : (option.descripcion || "")}
                                        value={listaCorridas.find((x) => x.descripcion === corridaSeleccionada) || corridaSeleccionada || null}
                                        inputValue={corridaInput}
                                        onChange={handleChangeCorrida}
                                        onInputChange={handleInputChangeCorrida}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Corrida (opcional):"
                                                onBlur={handleBlurCorrida}
                                                onKeyDown={handleOnKeyDownCorrida}
                                                helperText={corridaRango ? `Rango: ${corridaRango.medidainicial} - ${corridaRango.medidafinal}` : "Vacío = todas las medidas"}
                                            />
                                        )}
                                        sx={{ flex: 1 }}
                                    />
                                    {corridaSeleccionada && (
                                        <IconButton
                                            title="Limpiar corrida (usar todas las medidas)"
                                            aria-label="Limpiar corrida"
                                            size="small"
                                            onClick={handleLimpiarCorrida}
                                            sx={{ mt: '8px' }}
                                        >
                                            <CancelIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </Box>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={soloConExistencias}
                                            onChange={handleChangeSoloConExistencias}
                                            size="small"
                                        />
                                    }
                                    label="Solo con existencias"
                                    sx={{ minWidth: '190px', flex: 0, whiteSpace: 'nowrap' }}
                                />
                            </div>
                            <div className="body-Detalles-FrmExistencias">
                                {isPortatil && (
                                    <div className="detalle-descripcion-FrmExistencias">
                                        <TextField autoComplete="off"
                                            size="small"
                                            name="descripcion"
                                            label="Descripción:"
                                            value={descripcionSeleccionada}
                                            slotProps={{ input: { readOnly: true } }}
                                            sx={{ width: '100%' }}
                                        />
                                    </div>
                                )}
                                <SizeSoftXTableExistencias
                                    columns={[
                                        { title: "Clave artículo", field: "clavearticulo", hideSort: false,
                                          headerExtra: (
                                            <IconButton size="small" onClick={() => setShowClaveColumn(v => !v)} sx={{ p: 0.25 }}>
                                                {showClaveColumn ? <ArrowDropUpIcon fontSize="small" /> : <ArrowDropDownIcon fontSize="small" />}
                                            </IconButton>
                                          )
                                        },
                                        { title: "Descripción", field: "descripcion", hideSort: false,
                                          // 18/09/2026 | Ancho para portétil (~5cm): la tabla lo aplica
                                          // en modo scroll; en escritorio sigue la fija de 400px.
                                          width: "190px",
render: (row) => (
                                              <span style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.2, width: '100%' }}>
                                                  {row.descripcion}
                                              </span>
                                          )
                                        },
                                    ]}
                                    data={dataOrdenada}
                                    id="id"
                                    row={dataArticuloExistencia}
                                    setRow={handleArticuloSeleccionado}
                                    fontSize="13px"
                                    rowsPerPageIntro={50}
                                    activaProgress={statusWaiting}
                                    setClave={setClaveArticulo}
                                    setMedida={setMedida}
                                    disablePagination
                                    onActionClick={handleVerificarPreciosArticulo}
                                    showClaveColumn={showClaveColumn}
                                    disableStickyStartColumns={isPortatil}
                                />
                            </div>
                            <div className="body-pie-FrmExistencias">
                                <div className="lector-FrmExistencias">
                                    <SizeSoftXLectorCodigoBarrasGeneral
                                        statusBotonCarga={false}
                                        handleRegistrarArticulo={handleBuscarArticulo}
                                    />
                                </div>
                                <TextField autoComplete="off"
                                    size="small"
                                    name="totalregistros"
                                    label="Total registros:"
                                    value={totalRegistros}
                                    sx={{ width: '132px' }}
                                    disabled
                                />
                                <TextField autoComplete="off"
                                    size="small"
                                    name="totalpares"
                                    label="Total existencias:"
                                    value={cantidadArticulos}
                                    sx={{ width: '144px' }}
                                    disabled
                                />
                                <TextField autoComplete="off"
                                    size="small"
                                    name="clavearticulo"
                                    label="Clave artículo:"
                                    value={claveArticulo}
                                    sx={{ width: '250px' }}
                                    disabled
                                />
                                <TextField autoComplete="off"
                                    size="small"
                                    name="medida"
                                    label="Medida:"
                                    value={medida}
                                    sx={{ width: '110px' }}
                                    disabled
                                />
                                <TextField autoComplete="off"
                                    size="small"
                                    name="actualizado"
                                    label="Actualizado:"
                                    value={ultimaActualizacion ? ultimaActualizacion.toLocaleTimeString() : ""}
                                    sx={{ width: '110px' }}
                                    disabled
                                />
                            </div>
                        </div>
                </SizeSoftXLayoutForm>
                <SizeSoftXLoadingProcess
                    open={statusWaiting} message="Procesando información, por favor espere..."
                />
                <WhatsAppEnvioModal
                    openModal={openModalWhatsApp}
                    handleCloseModal={() => setOpenModalWhatsApp(false)}
                    articulo={dataArticuloExistencia}
                    almacenexhibidor={almacenexhibidor}
                    caja={parametrosCajaForm.caja}
                    idPhoneWhatsApp={parametrosCajaForm.idphonewhatsapp}
                    tokenAccesoWhatsApp={parametrosCajaForm.tokenaccesowhatsapp}
                />
                <SizeSoftXAlert />
            </Fragment>
        </>
    )
}
